import {
  PermissionCode,
  type OnboardingTaskListItem,
  type OnboardingTaskStatus,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ListPagination } from '@/components/list-pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { onboardingPanelClassName } from '@/products/onboarding/features/onboarding-panel';
import { OnboardingTaskFiles } from '@/products/onboarding/features/onboarding-task-files';
import { OnboardingTaskFields } from '@/products/onboarding/features/onboarding-task-fields';
import { onboardingFieldSubmission } from '@/products/onboarding/features/onboarding-task-field-utils';
import { OnboardingSystemActionFields } from '@/products/onboarding/features/onboarding-system-action-fields';
import {
  ONBOARDING_TASK_KIND_LABEL,
  ONBOARDING_TASK_STATUS_LABEL,
} from '@/products/onboarding/features/onboarding-template-labels';
import {
  useClaimOnboardingTask,
  useCompleteOnboardingTask,
  useOnboardingTasks,
  useUnclaimOnboardingTask,
} from '@/products/onboarding/features/use-onboarding-tasks';
import { onboardingCasePath } from '@/products/onboarding/onboarding-paths';

const PAGE_SIZE = 25;

export function OnboardingHomePage() {
  usePageTitle('Eintritt und Austritt');
  const me = useMe();
  const canReadCases = hasPermission(me.data, PermissionCode.ONBOARDING_CASES_READ);
  const canCompleteTasks = hasPermission(me.data, PermissionCode.ONBOARDING_TASKS_COMPLETE_OWN);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OnboardingTaskStatus | 'all'>('open');
  const tasks = useOnboardingTasks({
    page,
    pageSize: PAGE_SIZE,
    sortBy: 'dueOn',
    sortOrder: 'asc',
    ...(status === 'all' ? {} : { status }),
  });
  const complete = useCompleteOnboardingTask();
  const claim = useClaimOnboardingTask();
  const unclaim = useUnclaimOnboardingTask();
  const taskItems = tasks.data?.items;
  const busy = complete.isPending || claim.isPending || unclaim.isPending;

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Eintritt und Austritt"
        description="Aufgaben und Vorgänge für Eintritte und Austritte."
      />
      <div className={`${onboardingPanelClassName} flex flex-wrap items-end gap-3`}>
        <div className="w-44 space-y-2">
          <Label htmlFor="onboarding-home-status">Status</Label>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value as OnboardingTaskStatus | 'all');
              setPage(1);
            }}
          >
            <SelectTrigger id="onboarding-home-status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Offen</SelectItem>
              <SelectItem value="completed">Abgeschlossen</SelectItem>
              <SelectItem value="skipped">Übersprungen</SelectItem>
              <SelectItem value="not_applicable">Nicht zutreffend</SelectItem>
              <SelectItem value="cancelled">Storniert</SelectItem>
              <SelectItem value="all">Alle</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {tasks.isPending ? (
        <p className="text-sm text-muted-foreground">Aufgaben werden geladen …</p>
      ) : null}
      {tasks.isError ? (
        <p className="text-sm text-destructive">
          {tasks.error instanceof ApiRequestError
            ? tasks.error.message
            : 'Die Aufgaben konnten nicht geladen werden.'}
        </p>
      ) : null}
      {taskItems?.length === 0 ? (
        <p className={`${onboardingPanelClassName} text-sm text-muted-foreground`}>
          Noch keine Aufgaben vorhanden.
        </p>
      ) : null}
      {taskItems && taskItems.length > 0 ? (
        <ul className="space-y-4">
          {taskItems.map((task) => (
            <li key={task.id}>
              <TaskCard
                task={task}
                actorId={me.data?.id}
                showCaseLink={canReadCases}
                canComplete={canCompleteTasks}
                busy={busy}
                onClaim={() =>
                  claim
                    .mutateAsync({ taskId: task.id, revision: task.caseRevision })
                    .then(() => toast.success('Aufgabe übernommen.'))
                    .catch(showTaskError)
                }
                onUnclaim={() =>
                  unclaim
                    .mutateAsync({ taskId: task.id, revision: task.caseRevision })
                    .then(() => toast.success('Übernahme abgelegt.'))
                    .catch(showTaskError)
                }
                onComplete={async (result) => {
                  try {
                    await complete.mutateAsync({
                      taskId: task.id,
                      body: { revision: task.caseRevision, result },
                    });
                    toast.success('Aufgabe abgeschlossen.');
                  } catch (caught) {
                    showTaskError(caught);
                  }
                }}
              />
            </li>
          ))}
        </ul>
      ) : null}
      <ListPagination
        page={page}
        pageSize={PAGE_SIZE}
        total={tasks.data?.total ?? 0}
        onPageChange={setPage}
      />
    </div>
  );
}

function showTaskError(caught: unknown) {
  toast.error(
    caught instanceof ApiRequestError
      ? caught.message
      : 'Die Aufgabe konnte nicht bearbeitet werden.',
  );
}

function isPool(kind: OnboardingTaskListItem['assigneeKind']): boolean {
  return kind === 'role' || kind === 'department' || kind === 'team';
}

function isEquipment(kind: OnboardingTaskListItem['kind']): boolean {
  return (
    kind === 'asset_issue' ||
    kind === 'asset_return' ||
    kind === 'vehicle_assign' ||
    kind === 'vehicle_return'
  );
}

function TaskCard({
  task,
  actorId,
  showCaseLink,
  canComplete,
  busy,
  onClaim,
  onUnclaim,
  onComplete,
}: {
  task: OnboardingTaskListItem;
  actorId: string | undefined;
  showCaseLink: boolean;
  canComplete: boolean;
  busy: boolean;
  onClaim: () => void;
  onUnclaim: () => void;
  onComplete: (result: Record<string, unknown> | undefined) => Promise<void>;
}) {
  const isOpen = task.status === 'open';
  const pool = isPool(task.assigneeKind);
  const assignedToMe = pool ? task.claimedById === actorId : task.assigneeUserId === actorId;
  const canAct = task.canComplete ?? assignedToMe;
  const canTake = task.canClaim ?? (pool && !task.claimedById);
  const needsFields = task.kind === 'data' || task.kind === 'review';
  const equipment = isEquipment(task.kind);
  const [values, setValues] = useState<Record<string, unknown>>(() => task.result ?? {});
  const readOnly = !isOpen;

  return (
    <article className={`${onboardingPanelClassName} space-y-3`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">{task.title}</h2>
          <p className="text-sm text-muted-foreground">
            {ONBOARDING_TASK_KIND_LABEL[task.kind]} · {task.subjectDisplayName}
            {task.dueOn ? ` · Fällig ${formatDate(task.dueOn)}` : ''}
            {task.blocked ? ' · Blockiert' : ''}
            {pool && task.claimedById ? ' · Übernommen' : ''}
          </p>
        </div>
        <p className="text-sm text-muted-foreground">{ONBOARDING_TASK_STATUS_LABEL[task.status]}</p>
      </div>
      {task.instructions ? (
        <p className="text-sm whitespace-pre-wrap">{task.instructions}</p>
      ) : null}
      {showCaseLink && task.caseId ? (
        <p>
          <Link
            to={onboardingCasePath(task.caseId)}
            className="text-sm font-medium hover:underline"
          >
            Zum Vorgang
          </Link>
        </p>
      ) : null}
      {needsFields && task.dataFields.length > 0 ? (
        <OnboardingTaskFields
          mode="value"
          fields={task.dataFields}
          values={isOpen ? values : (task.result ?? {})}
          onChange={isOpen ? setValues : undefined}
          readOnly={readOnly}
        />
      ) : null}
      {isOpen && equipment && canAct && task.systemActionStatus !== 'succeeded' ? (
        <OnboardingSystemActionFields
          taskId={task.id}
          kind={task.kind}
          revision={task.caseRevision}
        />
      ) : null}
      {task.kind === 'document_upload' || task.kind === 'document_provide' ? (
        <OnboardingTaskFiles
          taskId={task.id}
          canUpload={Boolean(isOpen && canComplete && canAct)}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        {isOpen && canComplete && canTake && !task.claimedById && !task.blocked ? (
          <Button type="button" size="sm" disabled={busy} onClick={onClaim}>
            Übernehmen
          </Button>
        ) : null}
        {isOpen && canComplete && pool && task.claimedById === actorId ? (
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onUnclaim}>
            Ablegen
          </Button>
        ) : null}
        {isOpen &&
        canComplete &&
        !task.blocked &&
        canAct &&
        (!equipment || task.systemActionStatus === 'succeeded') ? (
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() =>
              void onComplete(
                needsFields ? onboardingFieldSubmission(task.dataFields, values) : undefined,
              )
            }
          >
            Abschließen
          </Button>
        ) : null}
      </div>
    </article>
  );
}
